import { supabase } from '@/lib/supabaseClient';
import { DiagramData } from './DiagramService';

export interface SearchResult {
  id: string;
  title: string;
  description: string;
  image?: string;
  type: 'course' | 'diagram';
  url: string;
  category?: string;
  difficulty?: string;
  matches?: string[];
  highlightMatches?: {
    title: string;
    description: string;
  };
}

export async function searchAll(query: string): Promise<SearchResult[]> {
  // Tạo regex để tìm matches cho highlighting
  const searchTerms = query.trim().split(/\s+/).filter(term => term.length > 2);
  const searchRegex = searchTerms.length > 0 
    ? new RegExp(`(${searchTerms.join('|')})`, 'gi') 
    : null;
  
  const [courses, diagrams] = await Promise.all([
    searchCourses(query),
    searchDiagrams(query)
  ]);
  
  // Thêm highlightMatches cho kết quả tìm kiếm
  const results = [...courses, ...diagrams];
  
  if (searchRegex) {
    return results.map(result => ({
      ...result,
      highlightMatches: {
        title: highlightMatchesInText(result.title, searchRegex),
        description: highlightMatchesInText(result.description, searchRegex)
      }
    }));
  }
  
  return results;
}

/**
 * Tạo phiên bản có đánh dấu từ khóa tìm kiếm trong chuỗi text
 * @param text Văn bản gốc
 * @param regex Biểu thức chính quy để tìm từ khóa
 * @returns Chuỗi văn bản với thẻ đánh dấu highlight
 */
function highlightMatchesInText(text: string, regex: RegExp): string {
  if (!text) return '';
  
  // Reset lastIndex để đảm bảo bắt đầu tìm từ đầu chuỗi
  regex.lastIndex = 0;
  
  return text.replace(regex, '<mark class="bg-yellow-200 dark:bg-yellow-900">$1</mark>');
}

export async function searchCourses(query: string): Promise<SearchResult[]> {
  if (!query.trim()) return [];
  
  const { data, error } = await supabase
    .from('courses')
    .select('id, name, description, image, category_id, difficulty')
    .or(`name.ilike.%${query}%, description.ilike.%${query}%`);
    
  if (error) {
    console.error('Error searching courses:', error);
    return [];
  }
  
  return data.map(course => ({
    id: course.id,
    title: course.name,
    description: course.description,
    image: course.image,
    type: 'course',
    url: `/courses/${course.id}`,
    category: course.category_id,
    difficulty: course.difficulty
  }));
}

export async function searchDiagrams(query: string): Promise<SearchResult[]> {
  if (!query.trim()) return [];
  
  const { data, error } = await supabase
    .from('diagrams')
    .select('id, name, description, image, category_id')
    .or(`name.ilike.%${query}%, description.ilike.%${query}%`);
    
  if (error) {
    console.error('Error searching diagrams:', error);
    return [];
  }
  
  return data.map(diagram => ({
    id: diagram.id,
    title: diagram.name,
    description: diagram.description,
    image: diagram.image,
    type: 'diagram',
    url: `/diagrams/${diagram.id}`,
    category: diagram.category_id
  }));
}

export async function fetchSuggestions(query: string): Promise<string[]> {
  if (!query.trim() || query.length < 2) return [];
  
  const { data: courseData } = await supabase
    .from('courses')
    .select('name')
    .ilike('name', `%${query}%`)
    .limit(5);
    
  const { data: diagramData } = await supabase
    .from('diagrams')
    .select('name')
    .ilike('name', `%${query}%`)
    .limit(5);
    
  const suggestions = [
    ...(courseData || []).map(c => c.name),
    ...(diagramData || []).map(d => d.name)
  ];
  
  return [...new Set(suggestions)].slice(0, 8);
} 